import styled from '@emotion/styled';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Fonts, mediaQuery, Spacings } from '@styles/index';
import PreviewButton from '@comp/PreviewButton/PreviewButton';
import { evtNameCategoryMap } from '@constants/index';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import { segment } from '@actions/analytics';
import PlayContained from 'aquilla/icons/actions/PlayContained';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getIntroVideoURL } from '@utils/textHelper';
import { BATCH_PREVIEW, BATCH_KEYS } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import Typography from 'aquilla/core/atoms/Typography';
import Tag from 'aquilla/core/atoms/Tag';
import { getBatchTag } from '@comp/BatchCard/util';

const Preview = styled(Typography)`
  margin-left: ${Spacings.SPACING_1B};
`;

const Intro = styled.iframe`
  height: 180px;
  width: 320px;
  border-radius: ${Spacings.SPACING_2B};
  margin-right: ${Spacings.SPACING_8B};
  background-color: var(--color-base-fill);
  transition: all 300ms ease;
`;

const HalfTriangle = styled.div`
  width: 0;
  height: 0;
  left: 10px;
  bottom: 38px;
  border-left: 6px solid transparent;
  border-bottom: 3px solid var(--color-i-black);
`;
const BatchTag = styled.div`
  display: flex;
  align-items: center;
  height: 22px;
  background-color: var(--color-text-primary);
  border-radius: ${Spacings.SPACING_1B};
  border-top-left-radius: 0px;
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_3B} ${Spacings.SPACING_0B}
    ${Spacings.SPACING_4B};
`;

const BatchTextTag = styled.span`
  font-size: ${Fonts.EXTRA_SMALL};
  color: var(--color-text-tertiary);
  font-weight: bold;
  line-height: 100%;
`;

const BatchTagWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  left: -6px;
  top: ${Spacings.SPACING_2B};
  height: 25px;
`;

const Dummy = styled.div`
  position: relative;
`;

const CourseThumbnail = styled(Thumbnail)`
  position: relative;
  height: ${({ height }) => height || 180}px;
  width: ${({ width }) => width || 320}px;
  margin-right: ${Spacings.SPACING_8B};
  border-radius: ${Spacings.SPACING_2B};
  transition: all 300ms ease-in-out;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-right: 0;
    height: ${({ height }) => height || 162}px;
    width: ${({ width }) => width || 320}px;
  }
`;

const BarsWrapper = styled.div`
  align-items: center;
`;

const Bar1 = styled.div`
  height: 3.2px;
  width: 50%;
  background: var(--color-divider);
  margin: ${Spacings.SPACING_1B} auto;
  border-radius: ${Spacings.SPACING_9B};
`;

const Bar2 = styled(Bar1)`
  width: 70%;
`;

const StyledPreviewButton = styled(PreviewButton)`
  background-color: var(--color-i-white);
  border-radius: 22px;
  color: var(--color-text-primary);
  padding: 6px ${Spacings.SPACING_3B} 6px 6px;

  :hover {
    background-color: var(--color-base-1);
  }
`;

const TagWrapper = styled.div`
  position: absolute;
  left: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  gap: ${Spacings.SPACING_3B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const StyledTag = styled(Tag)`
  padding: 4px 11px;
  > * {
    font-size: 12px !important;
    line-height: 12px !important;
  }
`;

const ScreenWrapper = styled.div`
  &.desktop {
    display: block;
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const BatchThumnail = ({
  batch,
  batchTag = true,
  onPreview,
  thumbnailHeight,
  thumbnailWidth,
  thumbnailPriority = true,
  langLabel = false,
  noCrop = false,
  barsWrapper = true
}) => {
  const batchGoalUID = batch?.goal?.uid;
  const { introVideo: preview, thumbnail, coverPhoto, languages } = batch;
  const resolvedThumbnail = thumbnail || coverPhoto || '';
  const goalSegmentData = useGoalEventProps({ batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batch,
    isBatch: true
  });
  const [showPreview, setShowPreview] = useState(false);
  const showIntro = () => {
    if (onPreview) onPreview();
    const lpss = segmentStorage.getItem('lpss') || 'Non Hero Batch Section';
    segment.track(
      BATCH_PREVIEW,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batch.state,
        start_date: batch.startsAt,
        end_date: batch.completedAt,
        ...(lpss && { last_primary_source_section: lpss })
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    if (lpss) segmentStorage.deleteItem('lpss');
    setShowPreview(true);
  };
  if (showPreview)
    return (
      <Intro
        className="intro-video"
        title="intro"
        src={getIntroVideoURL(preview)}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen="allowFullScreen"
      />
    );
  return (
    <Dummy>
      {barsWrapper && (
        <BarsWrapper>
          <Bar1 />
          <Bar2 />
        </BarsWrapper>
      )}
      <CourseThumbnail
        height={thumbnailHeight || 180}
        width={thumbnailWidth || 320}
        className="course-thumbnail"
        layout="responsive"
        imgUrl={resolvedThumbnail}
        priority={thumbnailPriority}
        sizes="(max-width: 768px) 100vw,
        (max-width: 1200px) 40vw,
        20vw"
        noCrop={noCrop}
      >
        {langLabel && (
          <ScreenWrapper className="desktop">
            <TagWrapper>
              <StyledTag type="hollow">
                <Typography
                  as="span"
                  variant="h6"
                  color="var(--color-text-primary)"
                  style={{ fontSize: '12px', lineHeight: '12px' }}
                >
                  {languages[0].label}
                </Typography>
              </StyledTag>
              <StyledTag type="hollow">
                <Typography
                  as="span"
                  variant="h6"
                  color="var(--color-text-primary)"
                >
                  {`${getBatchTag(batch)}${batch?.syllabusTag ? ' Batch' : ''}`}
                </Typography>
              </StyledTag>
            </TagWrapper>
          </ScreenWrapper>
        )}
        {preview && (
          <>
            <ScreenWrapper className="desktop">
              <StyledPreviewButton onClick={showIntro}>
                <PlayContained
                  height={Spacings.SPACING_6B}
                  width={Spacings.SPACING_6B}
                  color="var(--color-i-black)"
                />
                <Preview color="var(--color-i-black)" variant="h6">
                  Watch preview
                </Preview>
              </StyledPreviewButton>
            </ScreenWrapper>
            <ScreenWrapper className="mobile">
              <StyledPreviewButton onClick={showIntro}>
                <PlayContained
                  height={Spacings.SPACING_5B}
                  width={Spacings.SPACING_5B}
                  color="var(--color-i-black)"
                />
                <Preview color="var(--color-i-black)" variant="h6">
                  Watch preview
                </Preview>
              </StyledPreviewButton>
            </ScreenWrapper>
          </>
        )}
      </CourseThumbnail>
      {batchTag && (
        <BatchTagWrapper>
          <HalfTriangle />
          <BatchTag>
            <BatchTextTag>BATCH</BatchTextTag>
          </BatchTag>
        </BatchTagWrapper>
      )}
    </Dummy>
  );
};

export default BatchThumnail;

BatchThumnail.propTypes = {
  batch: PropTypes.objectOf(PropTypes.any).isRequired
};
